import { Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EMPTY, of, throwError } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import { StoreService } from "src/app/store.service";
import {
  Authorize,
  DecryptDealerId,
  DeleteStore,
  FirstTimeUser,
  GetCountries,
  GetCurrencies,
  GetCurrencyFormats,
  GetCustomers,
  GetDateDisplayFormat,
  GetFAQs,
  GetLanguages,
  GetNotifications,
  GetOrdersInfo,
  GetPlans,
  GetProducts,
  GetProductSortOrder,
  GetProfile,
  GetRecentOrders,
  GetStates,
  GetStoreInfo,
  GetStorePreferences,
  GetStores,
  GetTemplates,
  GetTimeDisplayFormat,
  GetTimezones,
  GetTotalOrders,
  GetTotalSales,
  GetUnits,
  GetUnreadNotifications,
  InvalidDealer,
  Login,
  MarkAllNotifAsRead,
  MarkItemsAsRead,
  ResumeStore,
  StoreLoading,
  StoreNotLoading,
  SuspendStore,
  SwitchStore,
  TimeoutError,
  UpdateCompanyAddress,
  UpdateCompanyInfo,
  UpdateLanguage,
  UpdateNotificationItem,
  UpdateSearchResults,
  VerifySSO
} from "./store.actions";

export interface StoreModel {
  validated: boolean,
  plans: any[];
  active: any;
  storesList: any[];
  userInfo: any;
  totalSales: any[],
  customers: any[],
  recentOrders: any[],
  totalOrders: any[],
  faqs: any,
  languages: any[],
  storeInfo: any,
  templates: any[],
  countries: any[],
  states: any[],
  units: any,
  currencies: any[],
  currencyFormats: any[],
  timezones: any[],
  timeFormat: any[],
  dateFormat: any[],
  productSortOrders: any[],
  orderInfo: any[],
  products: any[],
  searchResults: any,
  notifications: any[],
  unReadNotifications: any[],
  isFirstTimeUser: boolean
};

@State<StoreModel>({
  name: 'store',
  defaults: {
    validated: false,
    plans: [],
    active: null,
    storesList: [],
    userInfo: {
      username: 'test_dealer',
      password: 'C0mputerscience99',
      dId: '9780',
      enc_dId: 'RmtWK25RPT0-'
    },
    totalSales: [],
    customers: [],
    recentOrders: [],
    totalOrders: [],
    faqs: {},
    languages: [],
    storeInfo: null,
    templates: [],
    countries: [],
    states: [],
    units: null,
    currencies: [],
    currencyFormats: [],
    timezones: [],
    timeFormat: [],
    dateFormat: [],
    productSortOrders: [],
    orderInfo: [],
    products: [],
    searchResults: null,
    notifications: [],
    unReadNotifications: [],
    isFirstTimeUser: false
  }
})
@Injectable()
export class StoreState {

  constructor(
    private svc: StoreService,
    private router: Router,
    private ngZone: NgZone
  ) {
    sessionStorage.removeItem('5s-info');
  }

  @Selector()
  static validated(state:StoreModel) {
    return state.validated;
  }

  @Selector()
  static userInfo(state:StoreModel) {
    return state.userInfo;
  }

  @Selector()
  static plans(state:StoreModel) {
    return state.plans && state.plans.slice(0,3);
  }

  @Selector()
  static storesList(state:StoreModel) {
    return state.storesList;
  }

  @Selector()
  static activeStore(state:StoreModel) {
    const activeStore =  state.active || JSON.parse(localStorage.getItem('active-5s-store'));
    return activeStore;
  }

  @Selector()
  static totalSales(state:StoreModel) {
    return state.totalSales;
  }

  @Selector()
  static customers(state:StoreModel) {
    return state.customers;
  }

  @Selector()
  static recentOrders(state:StoreModel) {
    return state.recentOrders;
  }

  @Selector()
  static totalOrders(state:StoreModel) {
    return state.totalOrders;
  }

  @Selector()
  static faqs(state:StoreModel) {
    return state.faqs;
  }

  @Selector()
  static languages(state:StoreModel) {
    return state.languages;
  }

  @Selector()
  static storeInfo(state:StoreModel) {
    return state.storeInfo;
  }

  @Selector()
  static templates(state:StoreModel) {
    return state.templates;
  }

  @Selector()
  static countries(state:StoreModel) {
    return state.countries;
  }

  @Selector()
  static states(state:StoreModel) {
    return state.states;
  }

  @Selector()
  static units(state:StoreModel) {
    return state.units;
  }

  @Selector()
  static currencies(state:StoreModel) {
    return state.currencies;
  }

  @Selector()
  static currencyFormats(state:StoreModel) {
    return state.currencyFormats;
  }

  @Selector()
  static timezones(state:StoreModel) {
    return state.timezones;
  }

  @Selector()
  static timeFormat(state:StoreModel) {
    return state.timeFormat;
  }

  @Selector()
  static dateFormat(state:StoreModel) {
    return state.dateFormat;
  }

  @Selector()
  static productSortOrders(state:StoreModel) {
    return state.productSortOrders;
  }

  @Selector()
  static orderInfo(state:StoreModel) {
    return state.orderInfo;
  }

  @Selector()
  static products(state:StoreModel) {
    return state.products;
  }

  @Selector()
  static searchResults(state:StoreModel) {
    return state.searchResults;
  }

  @Selector()
  static notifications(state:StoreModel) {
    return state.notifications
  }

  @Selector()
  static unreadNotifications(state:StoreModel) {
    return state.unReadNotifications;
  }

  @Selector()
  static isFirstTimeUser(state:StoreModel) {
    return state.isFirstTimeUser;
  }

  @Action(Authorize)
  authorize(
    { patchState, dispatch }: StateContext<StoreModel>,
    { payload }: Authorize
  ) {
    patchState({ validated:payload });

    if(!payload) {
      localStorage.removeItem('5s-info');
      return dispatch(new InvalidDealer());
    }

    return of(null);
  }

  @Action(VerifySSO, { cancelUncompleted: true })
  verifySSO(
    { dispatch, getState }: StateContext<StoreModel>,
    { payload: {token, redirect_to, fromStore} }: VerifySSO
  ) {
    let info = null;
    localStorage.removeItem('active-5s-store');
    return this.svc.SSOVerify(token, redirect_to, fromStore).pipe(
      mergeMap((res:any) => {
        if(res && res.status_code == 'OK') {
          this.setSessionInfo(res.data);
          if (res.data?.feature !== undefined && res.data?.feature !== null && res.data?.feature){
            localStorage.setItem('feature',res.data.feature);
          }

          // return dispatch([
          //   new Authorize(true),
          //   new GetProfile(res.data.d_id)
          // ]);
          info = res.data;
          return dispatch(new Authorize(true));
        }

        return dispatch(new Authorize(false));
      }),
      mergeMap(_ => {
        if(getState().validated) {
          //return dispatch(new GetProfile(info.d_id));
            return dispatch(new Authorize(true));
        }
        return of(null);
      })
    );
  }

  @Action(Login, { cancelUncompleted: true })
  login(
    { dispatch }: StateContext<StoreModel>,
    { payload }: Login
  ) {

    return this.svc.login(payload.userInfo).pipe(
      mergeMap((res:any) => {
        if(res && res.code == 500) {
          throwError(res.message)
        } else if(res && res.data) {
          const info = {
            token: res.data.token,
            fullname: res.data.fullname,
            email: res.data.email
          };
          this.setSessionInfo(info);

          return dispatch(new GetProfile(payload.dId));
        }
        return of(null);
      })
    );
  }

  @Action(DecryptDealerId, { cancelUncompleted: true })
  decryptDealerId(
    { dispatch }: StateContext<StoreModel>,
    { payload }: DecryptDealerId
  ) {
    return dispatch(new Authorize(true))
  }

  @Action(GetPlans, { cancelUncompleted: true })
  getPlans(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().plans.length > 0) {
      return of(null);
    }

    return this.svc.getPlans().pipe(
      map((res:any) => {
        if(res && res.data) {
          patchState({ plans: res.data });
        }
      })
    );
  }

  @Action(GetProfile, { cancelUncompleted: true })
  getProfile(
    { patchState, getState, dispatch }: StateContext<StoreModel>,
    { payload }: GetProfile
  ) {
    return this.svc.getProfile(payload).pipe(
      catchError(err => throwError(err)),
      mergeMap((res:any) => {
        if(res && res.data) {
          const info = {
            ...getState().userInfo,
            profile: res.data
          };
          const sessInfo = this.getSessionInfo();
          sessInfo.profileId = res.data.profile_id;
          this.setSessionInfo(sessInfo);
          patchState({ userInfo: info });
        }
        return dispatch(new GetStores(null));
      })
    );
  }

  @Action(GetStores, { cancelUncompleted: true })
  getStores(
    { patchState }: StateContext<StoreModel>,
    { payload }: GetStores
  ) {
    return this.svc.getStoreList(payload).pipe(
      catchError(err => of(err)),
      mergeMap(res => {
        if(res && res.code == 'OK') {
          patchState({ storesList: res.data });
        }
        return of(null);
      })
    );
  }

  @Action(SwitchStore, { cancelUncompleted: true })
  switchStore(
    { patchState }: StateContext<StoreModel>,
    { payload }: SwitchStore
  ) {
    if(!payload) {
      return;
    }

    patchState({active:payload});
    localStorage.setItem('active-5s-store', JSON.stringify(payload));
  }

  @Action(DeleteStore)
  deleteStore(
    { patchState, getState,dispatch }: StateContext<StoreModel>,
    { payload }: DeleteStore
  ) {
    const stores = [...getState().storesList];
    const idx = stores.findIndex(item => item.store_id == payload.store_id);

    if(idx !== -1) {
      stores.splice(idx,1);

      patchState({storesList:stores});

      //Check if this store is the active store
      const activeStore = getState().active;
      if(payload.store_id == activeStore.store_id) {
        dispatch(new SwitchStore(stores[0]))
      }
    }
  }

  @Action(SuspendStore, { cancelUncompleted: true })
  suspendStore(
    { patchState, getState, dispatch }: StateContext<StoreModel>,
    { payload }: SuspendStore
  ) {
    return this.svc.suspendStore(payload).pipe(
      map(res => {
        if(res && res.code === 'OK') {
          const stores = getState().storesList.map(store => {
            return {
              ...store,
              is_suspended: store.store_id == payload ? '1' : store.is_suspended
            };
          })

          const activeStore = getState().active;
          if(activeStore.store_id == payload) {
            dispatch(new SwitchStore({
              ...activeStore,
              is_suspended: '1'
            }));
          }

          patchState({storesList:stores});
        }
      })
    )
  }

  @Action(ResumeStore, { cancelUncompleted: true })
  resumeStore(
    { patchState, getState, dispatch }: StateContext<StoreModel>,
    { payload }: ResumeStore
  ) {
    return this.svc.resumeStore(payload).pipe(
      map(res => {
        if(res && res.code === 'OK') {
          const stores = getState().storesList.map(store => {
            return {
              ...store,
              is_suspended: store.store_id == payload ? null : store.is_suspended
            };
          })

          const activeStore = getState().active;
          if(activeStore.store_id == payload) {
            dispatch(new SwitchStore({
              ...activeStore,
              is_suspended: null
            }));
          }
          patchState({storesList:stores});
        }
      })
    );
  }

  @Action(GetTotalSales, { cancelUncompleted: true })
  getTotalSales(
    { patchState }: StateContext<StoreModel>
  ) {
    const activeStore = this.getActiveStore();
    return activeStore && this.svc.getSalesInfo(activeStore.store_id).pipe(
      map((res:any) => {
        if(res && res.data) {
          patchState({ totalSales: res.data });
        }
      })
    ) || EMPTY;
  }

  @Action(GetCustomers, { cancelUncompleted: true })
  getCustomers(
    { patchState }: StateContext<StoreModel>
  ) {
    const activeStore = this.getActiveStore();
    return activeStore && this.svc.getCustomerInfo(activeStore.store_id).pipe(
      map((res:any) => {
        if(res && res.data) {
          patchState({ customers: res.data.data.items });
        }
      })
    ) || EMPTY;
  }

  @Action(GetRecentOrders, { cancelUncompleted: true })
  getRecentOrders(
    { patchState }: StateContext<StoreModel>
  ) {
    const activeStore = this.getActiveStore();
    return this.svc.getRecentOrders(activeStore.store_id).pipe(
      map((res:any) => {
        if(res && res.data) {
          patchState({ recentOrders: res.data });
        }
      })
    ) || EMPTY;
  }

  @Action(GetTotalOrders, { cancelUncompleted: true })
  getTotalOrders(
    { patchState }: StateContext<StoreModel>
  ) {
    const activeStore = this.getActiveStore();
    return this.svc.getTotalOrders(activeStore.store_id).pipe(
      map((res:any) => {
        if(res && res.data) {
          patchState({ totalOrders: res.data });
        }
      })
    ) || EMPTY;
  }

  @Action(GetFAQs, { cancelUncompleted: true })
  GetFAQs(
    { patchState }: StateContext<StoreModel>
  ) {
    const activeStore = this.getActiveStore();
    return this.svc.getFAQ().pipe(
      map((res:any) => {
        if(res && res.data) {
          patchState({ faqs: res.data });
        }
      })
    ) || EMPTY;
  }

  @Action(GetLanguages, { cancelUncompleted: true })
  getLanguages(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().languages.length > 0) {
      return;
    }

    const token = localStorage.getItem('bearer');
    return this.svc.getLanguageLists(token).pipe(
        catchError(err => of(err)),
        map((res:any) => {
            patchState({ languages: res['data'] });
        })
    );
  }

  @Action(UpdateLanguage, { cancelUncompleted: true })
  updateLanguage(
    { getState, patchState, dispatch }: StateContext<StoreModel>,
    { payload }: UpdateLanguage
  ) {

    const activeStore = this.getActiveStore();
    const params = {
      store_id: activeStore.store_id,
      dealer_id: getState().userInfo.dId,
      language_code: payload.code
    };

    return this.svc.updateLanguage(params).pipe(
      mergeMap((res:any) => {
        if(res && res.code == 'OK') {
          const newActiveStore = {
            ...getState().active,
            language: payload.code
          }

          const stores = [...getState().storesList];
          const idx = stores.findIndex(item => item.store_id == activeStore.store_id);

          if(idx !== -1) {
            stores.splice(idx, 1, newActiveStore);

            patchState({storesList:stores});
          }
          return dispatch(new SwitchStore(newActiveStore));
        }
        return of(null);
      })
    ) || EMPTY;
  }


  @Action(GetStoreInfo, { cancelUncompleted: true })
  GetStoreInfo(
    { getState, patchState }: StateContext<StoreModel>,
    { payload }: GetStoreInfo
  ) {

    return this.svc.getStoreProfile(payload.store_id).pipe(
      map(res => {
        if(res && res.code == 'OK') {
          const storeInfo = res.data.data;
          patchState({ storeInfo:storeInfo });
        }
      })
    )
  }

  @Action(UpdateCompanyInfo, { cancelUncompleted: true })
  updateCompanyInfo(
    { getState, patchState, dispatch }: StateContext<StoreModel>,
    { payload }: UpdateCompanyInfo
  ) {

    const activeStore = this.getActiveStore();
    const params = {
      ...payload,
      store_id: activeStore.store_id,
    };

    return this.svc.saveCompanyInfo(params).pipe(
      mergeMap(res => {
        if(res && res.code == 'OK') {
          return dispatch(new GetStoreInfo(getState().active));
        }
        return of(null);
      })
    ) || EMPTY;
  }

  @Action(GetTemplates, { cancelUncompleted: true })
  getTemplates(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().templates.length > 0) {
      return;
    }

    return this.svc.getStoreTemplates().pipe(
      map((res:any) => {
        if(res && res.code == 'OK') {
          patchState({ templates: res.data });
        }
      })
    );
  }

  @Action(GetCountries, { cancelUncompleted: true })
  getCountries(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().countries.length > 0) {
      return;
    }
    const token = localStorage.getItem('bearer');
    return this.svc.getCountryLists(token).pipe(
      catchError(err => of(err)),
      map((res:any) => {
        patchState({ countries: res['data'] });
      })
    );
  }

  @Action(GetStates, { cancelUncompleted: true })
  getStates(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().states.length > 0) {
      return;
    }

    return this.svc.getStates(null).pipe(
      map((res:any) => {
        patchState({ states: res });
      })
    );
  }

  @Action(GetUnits, { cancelUncompleted: true })
  getUnits(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().units) {
      return;
    }

    return this.svc.getUnits().pipe(
      map((res:any) => {
        patchState({ units: res });
      })
    );
  }

  @Action(UpdateCompanyAddress, { cancelUncompleted: true })
  updateCompanyAddress(
    { getState, patchState, dispatch }: StateContext<StoreModel>,
    { payload }: UpdateCompanyAddress
  ) {

    const activeStore = this.getActiveStore();
    const params = {
      ...payload,
      store_id: activeStore.store_id,
    };

    return activeStore && this.svc.saveCompanyAddress(params).pipe(
      mergeMap(res => {
        if(res && res.code == 'OK') {
          return dispatch(new GetStoreInfo(getState().active));
        }
        return of(null);
      })
    ) || EMPTY;
  }

  @Action(GetCurrencies, { cancelUncompleted: true })
  getCurrencies(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().currencies.length > 0) {
      return;
    }

    return this.svc.getCurrencies().pipe(
      map((res:any) => {
        patchState({ currencies: res });
      })
    );
  }

  @Action(GetCurrencyFormats, { cancelUncompleted: true })
  getCurrencyFormats(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().currencyFormats.length > 0) {
      return;
    }

    return this.svc.getCurrencyFormats().pipe(
      map((res:any) => {
        patchState({ currencyFormats: res });
      })
    );
  }

  @Action(GetTimezones, { cancelUncompleted: true })
  getTimezones(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().timezones.length > 0) {
      return;
    }

    return this.svc.getTimezones().pipe(
      map((res:any) => {
        patchState({ timezones: res });
      })
    );
  }

  @Action(GetTimeDisplayFormat, { cancelUncompleted: true })
  getTimeDisplayFormat(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().timeFormat.length > 0) {
      return;
    }

    return this.svc.getTimeFormats().pipe(
      map((res:any) => {
        patchState({ timeFormat: res });
      })
    );
  }

  @Action(GetDateDisplayFormat, { cancelUncompleted: true })
  getDateDisplayFormat(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().dateFormat.length > 0) {
      return;
    }

    return this.svc.getDateFormats().pipe(
      map((res:any) => {
        patchState({ dateFormat: res });
      })
    );
  }

  @Action(GetProductSortOrder, { cancelUncompleted: true })
  getProductSortOrder(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().productSortOrders.length > 0) {
      return;
    }

    return this.svc.getProductSortOrder().pipe(
      map((res:any) => {
        patchState({ productSortOrders: res });
      })
    );
  }

  @Action(GetOrdersInfo, { cancelUncompleted: true })
  getOrdersInfo(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    // if(getState().orderInfo.length > 0) {
    //   return;
    // }
    patchState({ orderInfo: [] });

    const activeStore = getState().active

    return activeStore && this.svc.getOrdersInfo(activeStore.store_id).pipe(
      map((res:any) => {
        if(res && res.code == 'OK' && res.data) {
          patchState({ orderInfo: res.data.data.items });
        }
      })
    );
  }

  @Action(GetProducts, { cancelUncompleted: true })
  getProducts(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    patchState({ products: [] });

    const activeStore = getState().active

    return activeStore && this.svc.getProducts(activeStore.store_id).pipe(
      map((res:any) => {
        if(res && res.code == 'OK' && res.data) {
          patchState({ products: res.data });
        }
      })
    );
  }

  @Action(UpdateSearchResults, { cancelUncompleted: true })
  updateSearchResults(
    { patchState }: StateContext<StoreModel>,
    { payload }: UpdateSearchResults
  ) {
    patchState( { searchResults:payload} );
  }

  @Action(GetNotifications, { cancelUncompleted: true })
  getNotifications(
    { patchState, getState }: StateContext<StoreModel>,
    { payload }: GetNotifications
  ) {
    if(getState().storesList.length == 0) {
      return;
    }

    const activeStore = getState().active;

    return activeStore && this.svc.getNotifications(activeStore.store_id, payload).pipe(
      catchError(err => {
        patchState({notifications:[]});
        return of(null);
      }),
      map(res => {
        if(res && res.code == 'OK') {
          patchState({ notifications:res.data });
        }
      })
    )
  }


  @Action(GetUnreadNotifications, { cancelUncompleted: true })
  getUnreadNotifications(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    if(getState().storesList.length == 0) {
      return;
    }

    const activeStore = getState().active;

    return activeStore && this.svc.getNotifications(activeStore.store_id, 0).pipe(
      catchError(err => {
        patchState({unReadNotifications:[]});
        return of(null);
      }),
      map(res => {
        if(res && res.code == 'OK') {
          patchState({ unReadNotifications:res.data });
        }
      })
    )
  }

  @Action(UpdateNotificationItem, { cancelUncompleted: true })
  updateNotificationItem(
    { patchState, getState }: StateContext<StoreModel>,
    { payload }: UpdateNotificationItem
  ) {
    return this.svc.updateNotificationItem(payload.id).pipe(
      map(res => {
        if(res && res.code == 'OK') {
          const list = [...getState().unReadNotifications];
          const itemIdx = list.findIndex(item => item.id == payload.id);
          list.splice(itemIdx, 1);

          patchState({ unReadNotifications:list });
        }
      })
    )
  }

  @Action(MarkAllNotifAsRead, { cancelUncompleted: true })
  markAllNotifAsRead(
    { patchState, getState }: StateContext<StoreModel>
  ) {
    const itemIds = getState().unReadNotifications.map(item => item.id);

    return this.svc.markAllNotificationsAsRead(itemIds.join(',')).pipe(
      map(res => {
        if(res && res.code == 'OK') {
          patchState({ unReadNotifications:[] });
        }
      })
    )
  }

  @Action(MarkItemsAsRead, { cancelUncompleted: true })
  MarkItemsAsRead(
    { patchState, getState }: StateContext<StoreModel>,
    { payload }: MarkItemsAsRead
  ) {
    const itemIds = payload.map(item => item.id);

    return this.svc.markAllNotificationsAsRead(itemIds.join(',')).pipe(
      map(res => {
        if(res && res.code == 'OK') {
          const filtered = getState().unReadNotifications.filter(item => itemIds.includes(item.id));
          patchState({ unReadNotifications:filtered });
        }
      })
    )
  }

  @Action(InvalidDealer)
  invalidDealer(
    { dispatch, patchState }: StateContext<StoreModel>
  ) {
    // return dispatch(new Authorize(false)).pipe(
    //   mergeMap(_ => {
    //     this.ngZone.run(_ => this.router.navigate(['access-denied']));
    //     return of(null);
    //   })
    // );
    patchState({ validated:false });
    this.ngZone.run(_ => this.router.navigate(['access-denied']));
    return of(null);
  }

  @Action(TimeoutError)
  timeout() {}

  @Action(StoreLoading)
  storeLoading() {}

  @Action(StoreNotLoading)
  storeNotLoading() {}

  @Action(FirstTimeUser)
  firstTimeUser({patchState}: StateContext<StoreModel>) {
    console.log('first user')
    patchState({isFirstTimeUser:true});
  }

  //////////////////////////////////////
  private getActiveStore() {
    return JSON.parse(localStorage.getItem('active-5s-store'));
  }

  private getSessionInfo() {
    return JSON.parse(localStorage.getItem('5s-info'));
  }

  private setSessionInfo(info) {
    localStorage.setItem('5s-info', JSON.stringify(info));
  }
}
